import React from "react";
import { Autocomplete, Grid, Paper, TextField, Typography } from "@mui/material";
import { LabeledField } from "@/__core/components/LabeledField";
import { sharedFormStyles } from "@/__core/styles/sharedFormStyles";
import { SalaryConfigGroup } from "@/modules/settings/redux/SettingsCRUD";

interface AccountSalaryProps {
  salaryConfigGroups: SalaryConfigGroup[];
  salaryConfigGroup: string | SalaryConfigGroup | null;
  salaryConfigGroupsSecondary: Array<string | SalaryConfigGroup>;
  bankAccountName: string;
  bankAccountNumber: string;
  bankName: string;
  canEdit: boolean;
  onChange: (field: string, value: any) => void;
}

const getSalaryConfigGroupId = (value: string | number | SalaryConfigGroup | null | undefined) => {
  if (!value) return "";
  if (typeof value === "object") return String(value.id || "");
  return String(value);
};

const AccountSalary: React.FC<AccountSalaryProps> = ({
  salaryConfigGroups,
  salaryConfigGroup,
  salaryConfigGroupsSecondary,
  bankAccountName,
  bankAccountNumber,
  bankName,
  canEdit,
  onChange,
}) => {
  const selectedSalaryConfigGroupId = getSalaryConfigGroupId(salaryConfigGroup);
  const selectedSalaryConfigGroupSecondaryIds = (salaryConfigGroupsSecondary || []).map(getSalaryConfigGroupId);

  return (
    <Paper sx={{ ...sharedFormStyles.formCard, mb: 2 }}>
      <Typography sx={sharedFormStyles.sectionTitle}>Thông tin tài khoản nhận lương</Typography>
      <Grid container rowSpacing={1} columnSpacing={5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label="CT lương chính" labelWidth={120}>
            <Autocomplete
              fullWidth
              options={salaryConfigGroups}
              getOptionLabel={(option) => option.name || ""}
              value={salaryConfigGroups.find((g) => String(g.id) === selectedSalaryConfigGroupId) || null}
              onChange={(_, newValue) =>
                onChange("salary_config_group", newValue ? String(newValue.id) : "")
              }
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              disabled={!canEdit}
              size="small"
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Chọn công thức lương chính" disabled={!canEdit} />
              )}
            />
          </LabeledField>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label="CT lương phụ" labelWidth={120}>
            <Autocomplete
              multiple
              fullWidth
              options={salaryConfigGroups}
              getOptionLabel={(option) => option.name || ""}
              value={salaryConfigGroups.filter((g) =>
                selectedSalaryConfigGroupSecondaryIds.includes(String(g.id))
              )}
              onChange={(_, newValue) =>
                onChange("salary_config_groups_secondary", newValue.map((v) => String(v.id)))
              }
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              disabled={!canEdit}
              size="small"
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Chọn công thức lương phụ" disabled={!canEdit} />
              )}
            />
          </LabeledField>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={5}>
        <Grid size={{ xs: 12, md: 4 }}>
          <LabeledField label="Tên tài khoản" labelWidth={100}>
            <TextField
              fullWidth
              size="small"
              value={bankAccountName}
              onChange={(e) => onChange("bank_account_name", e.target.value)}
              disabled={!canEdit}
            />
          </LabeledField>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <LabeledField label="Số tài khoản" labelWidth={100}>
            <TextField
              fullWidth
              size="small"
              value={bankAccountNumber}
              onChange={(e) => onChange("bank_account_number", e.target.value)}
              disabled={!canEdit}
              inputProps={{ inputMode: "numeric" }}
            />
          </LabeledField>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <LabeledField label="Ngân hàng" labelWidth={80}>
            <TextField
              fullWidth
              size="small"
              value={bankName}
              onChange={(e) => onChange("bank_name", e.target.value)}
              disabled={!canEdit}
            />
          </LabeledField>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AccountSalary;
